import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Navigate, useLocation } from "react-router-dom";
import { APP_ENTRY_ROUTE, clearAccessFlowLock, getAccessFlowLock } from "@/lib/access-flow";
import { toast } from "sonner";
import { traceRouteEvent } from "@/lib/route-debug-trace";

interface OnboardingGuardProps {
  children: React.ReactNode;
}

type GuardOutcome = {
  isMinorBlocked: boolean;
  isSuspended: boolean;
  needsAgeVerification: boolean;
  needsOnboarding: boolean;
};

const GUARD_TIMEOUT_MS = 5000;

const withTimeout = async <T,>(promise: PromiseLike<T>, fallback: T, timeoutMs = GUARD_TIMEOUT_MS): Promise<T> => {
  let timeoutId: number | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((resolve) => {
        timeoutId = window.setTimeout(() => resolve(fallback), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
  }
};

const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const [outcome, setOutcome] = useState<GuardOutcome>({
    isMinorBlocked: false,
    isSuspended: false,
    needsAgeVerification: false,
    needsOnboarding: false,
  });

  useEffect(() => {
    if (loading || !user) return;

    // Onboarding/age-verification só se aplica ao fluxo de USUÁRIO comum.
    const lockedFlow = getAccessFlowLock();
    if (lockedFlow && lockedFlow !== "user") {
      setChecked(true);
      return;
    }

    let cancelled = false;
    const checkProfile = async () => {
      // Parallel: profile fields + photo presence check (head=true → cheap)
      const [profileRes, photosRes] = await withTimeout(
        Promise.all([
          // get_own_profile() RPC (SECURITY DEFINER) — necessário porque
          // birth_date e verification_selfie_url tiveram SELECT revogado
          // do role authenticated para evitar leak via discover policy.
          // NÃO usamos .maybeSingle() aqui: a RPC retorna SETOF e queremos
          // tolerância a falhas parciais (pegamos data[0] ou fallback).
          supabase.rpc("get_own_profile"),
          supabase
            .from("profile_photos")
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id),
        ]),
        [{ data: null, error: { message: "timeout" } }, { data: null, count: null, error: { message: "timeout" } }] as any
      );

      if (cancelled) return;

      const profileRow: any = Array.isArray(profileRes?.data)
        ? profileRes.data[0]
        : profileRes?.data ?? null;
      const profileFetchFailed = !!profileRes?.error || profileRow == null;
      const photosFetchFailed = !!photosRes?.error;
      const count = photosRes?.count;
      const hasName = !!profileRow?.name && String(profileRow.name).trim().length > 0;
      const hasAvatar = !!profileRow?.avatar_url && String(profileRow.avatar_url).trim().length > 0;
      const hasPhoto = (count ?? 0) > 0 || hasAvatar;
      const status = profileRow?.account_status;

      const next: GuardOutcome = {
        isMinorBlocked: status === "minor_blocked",
        isSuspended: status === "suspended_minority" || status === "banned",
        // Só redireciona quando o perfil foi carregado e confirma a pendência.
        // Em falhas transitórias, a proteção server-side continua valendo.
        needsAgeVerification:
          !profileFetchFailed &&
          (profileRow?.age_verified !== true || status !== "age_verified"),
        needsOnboarding: false,
      };

      // Onboarding só é exigido quando conseguimos confirmar que faltam
      // dados. Se a consulta falhou/retornou null, NÃO bloqueamos — evita
      // falso positivo do toast "Adicione seu nome e uma foto de perfil"
      // quando o perfil já está completo mas o RPC não respondeu.
      if (
        !next.isMinorBlocked &&
        !next.isSuspended &&
        !next.needsAgeVerification &&
        !profileFetchFailed &&
        !photosFetchFailed &&
        (!hasName || !hasPhoto)
      ) {
        next.needsOnboarding = true;
      }

      setOutcome(next);
      setChecked(true);
    };

    checkProfile();
    return () => {
      cancelled = true;
    };
  }, [user, loading]);

  const renderedState = loading
    ? "children:auth-loading-stale"
    : !checked
      ? "children:onboarding-pending-stale"
      : outcome.isMinorBlocked
        ? "redirect:minor-blocked"
        : outcome.isSuspended
          ? "redirect:suspended"
          : outcome.needsAgeVerification
            ? "redirect:verify-age"
            : outcome.needsOnboarding
              ? "redirect:profile-onboarding"
              : "children:allowed";

  useEffect(() => {
    traceRouteEvent("OnboardingGuard:state", `${location.pathname}${location.search}`, {
      loading,
      checked,
      user: user ? "present" : "none",
      outcome,
      renderedState,
    });
  }, [checked, loading, location.pathname, location.search, outcome, renderedState, user]);

  if (loading) return <>{children}</>;
  if (!checked) return <>{children}</>;

  if (outcome.isMinorBlocked) {
    return <Navigate to="/minor-blocked" replace />;
  }

  if (outcome.isSuspended) {
    return <Navigate to="/minor-blocked" replace />;
  }

  if (outcome.needsAgeVerification) {
    return <Navigate to="/verify-age" replace />;
  }

  if (outcome.needsOnboarding) {
    if (typeof window !== "undefined" && window.location.pathname !== "/profile") {
      toast.info("Adicione seu nome e uma foto de perfil para liberar a navegação.");
    }
    return <Navigate to="/profile" replace />;
  }

  return <>{children}</>;
};

export default OnboardingGuard;
